/** @format */

import React, {
  ComponentPropsWithoutRef,
  ComponentType,
  ReactElement,
} from 'react'

import {PluginKind} from '@src/types/Plugin'
import {PassThroughFC} from '@src/typeUtils'

/** @format */

type AppLogoProps = ComponentPropsWithoutRef<'i'> & {
  fontSize?: string
}

const AppLogo: React.FC<
  AppLogoProps & {
    content: string | ReactElement<'svg'>
  }
> = ({content, fontSize = 'xx-large', ...rest}) => {
  return (
    <i
      {...rest}
      style={{
        display: 'inline-block',
        fontFamily: 'logofont',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize,
        lineHeight: '1',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      {content}
    </i>
  )
}
export const ActiveCampaignLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e919)} />
}
export const AirtableLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e907)} />
}
export const AsanaLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e917)} />
}
export const ConstantContactLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e916)} />
}
export const ConvertkitLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e909)} />
}

export const DiscordLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90b)} />
}

export const FreshdeskLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90c)} />
}

export const GithubLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e913)} />
}

export const GladlyLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e910)} />
}

export const GoogleLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e900)} />
}

export const GorgiasLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90d)} />
}

export const HubspotLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e908)} />
}

export const KlaviyoLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e911)} />
}

export const MailchimpLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e901)} />
}

export const MailIcon: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e912)} />
}

export const NotionLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e914)} />
}

export const MailerliteLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90a)} />
}

export const PipedriveLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90e)} />
}

export const RedshiftLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e915)} />
}

export const SalesforceLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e918)} />
}

export const SlackLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e905)} />
}

export const StripeLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e904)} />
}

export const TelegramLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90f)} />
}

export const LinearLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e91c)} />
}

export const TrelloLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e902)} />
}

export const ZapierLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e906)} />
}

export const ZendeskLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e903)} />
}

export const ZohoCrmLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e91b)} />
}

export function getAppLogoByPluginKind(
  pluginKind: PluginKind,
): ComponentType<AppLogoProps> | null {
  return appLogoByPluginKind[pluginKind]
}

const appLogoByPluginKind: Readonly<
  Record<PluginKind, ComponentType<AppLogoProps> | null>
> = Object.freeze({
  airtable: AirtableLogo,
  asana: AsanaLogo,
  autoresponse: null,
  constantcontact: ConstantContactLogo,
  convertkit: ConvertkitLogo,
  discord: DiscordLogo,
  email: MailIcon,
  freshdesk: FreshdeskLogo,
  github: GithubLogo,
  gladly: GladlyLogo,
  'google-sheets': GoogleLogo,
  gorgias: GorgiasLogo,
  hubspot: HubspotLogo,
  activecampaign: ActiveCampaignLogo,
  klaviyo: KlaviyoLogo,
  linear: LinearLogo,
  mailchimp: MailchimpLogo,
  mailerlite: MailerliteLogo,
  notion: NotionLogo,
  pipedrive: PipedriveLogo,
  redshift: RedshiftLogo,
  salesforce: SalesforceLogo,
  slack: SlackLogo,
  stripe: StripeLogo,
  stripe_v2: StripeLogo,
  telegram: TelegramLogo,
  trello: TrelloLogo,
  webhook: null,
  zapier: ZapierLogo,
  zendesk: ZendeskLogo,
  zohocrm: ZohoCrmLogo,
})
